import { Inject, Injectable } from '@angular/core';
import { APP_STORE_CONFIG_TOKEN, MODELS_STORE_NAME_TOKEN } from '@ddt-web/config';
import { InjectConfig, PaConfigKey, SharedConfigKey } from '@ddt-web/config-layer';
import { AppStoreConfig, PositionDetails } from '@ddt-web/data-service';
import { AdditionalAnalysisParams } from '@ddt-web/domain';
import { ApplicationStateStoreProxyService } from '@ddt-web/shared-stores';
import { StoreService } from '@ddt-web/store-service';
import { PaQueryParamKey } from '@ddt-web/url';
import * as castArray from 'lodash/castArray';
import * as get from 'lodash/get';
import { combineLatest } from 'rxjs';
import { first } from 'rxjs/operators';

import { AbstractWorker } from '../models/abstract-worker';
import { PasDataLoaderService } from '../pas-setup-data-workers';
import { PasPositionDetailsLoadedProcessingService } from '../pas-setup-data-workers/services';
import { PortfolioResolverUtil } from '../utils/portfolio-resolver.util';

/**
 * Worker used by 360 evaluator
 * Takes care of making the right WDS and WWE API calls
 */
@Injectable()
export class DataLoadingWorker extends AbstractWorker {
	@InjectConfig(PaConfigKey.ENABLE_FUND_STATS) private readonly isFundStatsEnabled: boolean = false;
	@InjectConfig(SharedConfigKey.ENABLE_TAX_MIGRATION) private readonly enableTaxMigration = false;
	@InjectConfig(SharedConfigKey.AteShowTaxSavingsInsights) private readonly isTaxEstimatorInsightVisible = false;

	constructor(
		public storeService: StoreService,
		protected pasDataLoaderService: PasDataLoaderService,
		protected pasPositionDetailsLoadedProcessingService: PasPositionDetailsLoadedProcessingService,
		private applicationStateProxyService: ApplicationStateStoreProxyService,
		@Inject(APP_STORE_CONFIG_TOKEN) public storeConfig: AppStoreConfig,
		@Inject(MODELS_STORE_NAME_TOKEN) public modelsStoreName: string
	) {
		super();
	}

	public handle(queryParams: any): void {
		const ids = castArray(queryParams?.portfolios ?? []);
		const benchmarkId = get(queryParams, PaQueryParamKey.AC_BENCHMARK);
		const internalPortfolios = PortfolioResolverUtil.extractInternalPortfolioIds(ids);
		const partnerPortfolios = PortfolioResolverUtil.extractPartnerPortfolioIds(ids);
		const externalPortfolios = PortfolioResolverUtil.extractExternalPortfolioIds(ids);

		combineLatest([
			this.storeService.selectAll(this.storeConfig[this.modelsStoreName]),
			this.storeService.selectByIds(this.storeConfig.associatedPortfolios, ids),
			this.applicationStateProxyService.getImportPartnerPortfoliosState()
		]).pipe(
			first(([config, associatedPortfolios, importPartnerPortfolioState]: any) => {
				const isPartnerPortfoliosSuccessfullyLoaded = PortfolioResolverUtil.isPortfoliosSuccessfullyLoaded(partnerPortfolios, config, associatedPortfolios);
				const isInternalPortfoliosSuccessfullyLoaded = PortfolioResolverUtil.isPortfoliosSuccessfullyLoaded(internalPortfolios, config, associatedPortfolios);
				const isExternalPortfoliosSuccessfullyLoaded = PortfolioResolverUtil.isPortfoliosSuccessfullyLoaded(externalPortfolios, config, associatedPortfolios);
				const isRefreshNeeded = importPartnerPortfolioState.isRefreshNeeded;

				return config
					&& !config.isLoading
					&& !associatedPortfolios.isLoading
					&& !isRefreshNeeded
					&& isPartnerPortfoliosSuccessfullyLoaded
					&& isInternalPortfoliosSuccessfullyLoaded
					&& isExternalPortfoliosSuccessfullyLoaded;
			})
		).subscribe(([config, associatedPortfolios]) => {
			const additionalAnalysisParams = {
				currency: 'USD', identifierToBeUsedAsWweApiParamId: 'identifier'
			};

			if (!benchmarkId) {
				this.requestPortfolioDetails(ids, config, associatedPortfolios, additionalAnalysisParams);
			}

			if (this.isFundStatsEnabled) {
				this.pasDataLoaderService.populateStoresForPositions(ids, true, additionalAnalysisParams, null, [this.pasPositionDetailsLoadedProcessingService]);
			} else {
				this.pasDataLoaderService.populateStoresForPositions(ids, true, additionalAnalysisParams);
			}
		});
	}

	protected requestPortfolioDetails(ids, config, associatedPortfolios, additionalAnalysisParams?: AdditionalAnalysisParams): void {
		this.pasDataLoaderService.populateStoresForPortfolios(ids, config.entities, associatedPortfolios, additionalAnalysisParams);

		if (this.enableTaxMigration && this.isTaxEstimatorInsightVisible) {
			this.pasDataLoaderService.populateStoresForPositions(
				ids, false, additionalAnalysisParams, null, null, null, PositionDetails.SECURITY_DETAILS_API_TYPE.SECURITY_DETAILS_TAX
			);
		}
	}
}
